import { getBackendUrl } from "@Utils";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ToolTipComponent from "../../../cubeComponent/html/tooltip";
import axiosWrapper from "../../../services/axiosWrapper";
import { showToastNotification } from "../../../actions/toast";
import { TOAST_TYPE_SUCCESS, TOAST_TYPE_ERROR } from "../../../services/constants";
import FileDelete from "../FileDelete/FileDelete";
import { saveCustomFieldsData, removeFileUploadData, updateFileUploadData } from "../../../actions/fileUpload";
import { ProgressBarComponent } from "@syncfusion/ej2-react-progressbar";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import _ from "lodash";
import useCustomFields, { NomenclatureField } from "../../../services/utils/useCustomFields";
import { MdDeleteOutline, MdOutlineEdit, MdCheckCircleOutline } from "react-icons/md";
import { FaRegSave } from "react-icons/fa";
import { BsExclamationCircle } from "react-icons/bs";
import Upload from "../../../assets/icons/svg/upload.svg";
import { useNavigate } from "react-router-dom";
import useForms from "../../CreateInspectionAndProgress/useForms";
import FormQuestionFields from "../../CreateInspectionAndProgress/questionFields";
import { addValidationToForms } from "../../CreateInspectionAndProgress/constants";

import { isFileNomenclatureCorrect } from "../../../services/utils/helpers";
import { getFileImage } from "../../../services/utils/iconMap";
import * as React from "react";
import FilesTable from "./FilesTable";
import PropTypes from "prop-types";

const FilesGrid = ({
  template,
  formTemplateId,
  workspaceId,
  setEnableDone,
  fileNomenclature,
  batchId,
  isFolderUpload,
  formValidator,
}) => {
  const { fileUploadList } = useSelector((state) => state);
  const { t } = useTranslation();
  const uploadType = {
    UPLOAD_AS_NEW_FILE: "uploadAsNewFile",
    UPLOAD_AS_NEW_VERSION: "uploadAsNewVersion",
  };
  const dispatch = useDispatch();

  const updateFileData = (id, updater) => {
    const index = id - 1;
    const current = fileUploadList[index] || {};
    const data = typeof updater === "function" ? updater(current) : updater;
    dispatch(
      updateFileUploadData({
        index,
        data,
      }),
    );
  };

  const [openDeleteConfirmationModal, setOpenDeleteConfirmationModal] = useState(false);
  const { isFileVersionUploading } = useSelector((state) => state);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [isBulk, setIsBulk] = useState(false);
  const [bulkData, setBulkData] = useState({ id: 0, progress: 0, extension: null, name: "", isBulkEdit: true });
  const [query, setQuery] = useState(null);
  const [debounced, setDebounced] = useState(null);

  const { validateFormWithId } = useCustomFields();

  const navigate = useNavigate();

  const {
    handleURLModalState,
    isFileUploading,
    handleFileUploadChange,
    removeUserFromList,
    saveEditedImage,
    handleRemoveUploadedFile,
    openAddUserModal,
    handleInputChange,
    handleSaveUrlData,
    showUrlModal,
    setURLData,
    spaceId,
    showGeoLocationModal,
    setShowGeoLocationModal,
    handleGeoLocationModalState,
  } = useForms({ navigate });

  const filesData = React.useMemo(
    () =>
      fileUploadList.map((file, index) => ({
        ...file,
        id: index + 1,
        _id: file.fileId,
      })),
    [fileUploadList],
  );

  const visibleFiles = React.useMemo(() => {
    const base = filesData.filter((f) => !f.isDeleted);
    return isBulk ? [bulkData, ...base] : base;
  }, [filesData, isBulk, bulkData]);

  const selectedFiles = React.useMemo(
    () => filesData.filter((file) => !file.isDeleted).filter((file) => selectedFileIds.includes(file.id)),
    [filesData, selectedFileIds],
  );
  const hasDuplicateSelected = React.useMemo(() => selectedFiles.some((file) => file.isDuplicate), [selectedFiles]);

  const disableDeleteButton = selectedFileIds.length === 0;
  const disableBulkUploadButton = disableDeleteButton || hasDuplicateSelected;

  useEffect(() => {
    const rows = isBulk ? visibleFiles.slice(1) : visibleFiles;
    addValidationToForms({
      formValidator,
      template,
      rows,
    });
  }, [visibleFiles.length, isBulk]);

  useEffect(() => {
    const handler = setTimeout(() => setDebounced(query), 500);
    return () => clearTimeout(handler);
  }, [query]);

  useEffect(() => {
    if (debounced === undefined || debounced === null) {
      return;
    }

    const { id, eachField, value } = debounced;
    saveCustomFields(id, eachField, value);
  }, [debounced]);

  const gridNameTemplate = (file, id) => {
    const fileData = fileUploadList[id - 1] || {};
    const error = fileData.error || "";

    let fileValidationTooltip = null;

    if (!isFileVersionUploading && fileData) {
      if (!fileData.isFileNomenclatureCorrect) {
        fileValidationTooltip = (
          <ToolTipComponent
            showTipPointer
            position="bottom"
            content={t(
              "File name does not comply with the nomenclature set for this folder. Please rename the file or visit Escrow files section.",
            )}
          >
            <span>
              <BsExclamationCircle className="w-3 h-3 text-red-600" />
            </span>
          </ToolTipComponent>
        );
      } else if (fileData.isDuplicate && !fileData?.nomenclature?.length) {
        fileValidationTooltip = (
          <ToolTipComponent
            showTipPointer
            position="bottom"
            content={
              file.isFolder
                ? t("Folder with same name already exists. Please rename and try again.")
                : t("File with same name already exists. Upload as new file or version.")
            }
          >
            <span>
              <BsExclamationCircle className="w-3 h-3 text-red-600" />
            </span>
          </ToolTipComponent>
        );
      }
    }

    return (
      <div className="flex flex-col gap-1 w-full">
        <div className="flex items-center justify-between w-full gap-2">
          <div className="flex items-center min-w-0">
            <img src={getFileImage(file)} alt="file" className="w-5 h-5 shrink-0" />
            <span className="ms-1 truncate text-sm">{file.name}</span>
          </div>
          {fileValidationTooltip}
        </div>
        {error && (
          <div id={`error_text_${id}`} className="text-xs text-red-500 break-words">
            {t(error)}
          </div>
        )}
      </div>
    );
  };

  const progressTemplate = (file, id) => {
    const progress = file.progress || 0;

    let displayProgress = progress;
    if (progress === 100 && !file.progressCompleted) {
      displayProgress = 99;
    }

    return (
      <div className="flex items-center gap-2 w-full">
        <div className="flex-1 min-w-0 overflow-hidden">
          <ProgressBarComponent
            value={progress}
            width="100%"
            height="20"
            showProgressValue={false}
            id={`progress_${id}`}
          />
        </div>
        <span className="flex-shrink-0 text-sm text-end min-w-[32px] whitespace-nowrap">{`${displayProgress}%`}</span>
      </div>
    );
  };

  const gridNomenclatureTemplate = (file, id) => {
    if (file?.isFolder || !fileNomenclature?.fields?.length) {
      return null;
    }
    const nomenclature = file.nomenclature || [];
    return (
      <div className="flex gap-2 items-center">
        {fileNomenclature.fields.map((field, index) => {
          const value = nomenclature[index] || "";
          return (
            <NomenclatureField
              key={`${id}-${field._id}-${index}`}
              args={{ ...file, id }}
              field={field}
              index={index}
              value={value}
              handleNomenclatureChange={handleNomenclatureChange}
            />
          );
        })}
      </div>
    );
  };

  const gridCustomFieldTemplate = (field, id) => {
    const placeholderText = `Enter ${field.questionValue}`;
    const fileData = id !== 0 ? fileUploadList[id - 1] || {} : bulkData || {};
    const value = fileData[field._id] || "";
    const processedValue = field.fieldType === "multi-select" ? asArray(value) : value;

    const handleSubmit = (e) => {
      e.preventDefault();
    };

    return (
      <form
        className="flex justify-start items-center w-full h-full"
        aria-labelledby={`field-${field._id}`}
        onSubmit={handleSubmit}
      >
        <fieldset style={{ width: "100%" }} aria-labelledby={`field-${field._id}`}>
          <legend id={`field-${field._id}`} className="sr-only">
            {field.questionValue}
          </legend>
          <FormQuestionFields
            extraParams={{ id }}
            eachField={{
              ...field,
              answer: { value: processedValue },
              placeholderText,
            }}
            width="100%"
            handleCustomFieldData={handleCustomFieldData}
            multiTextRows={1}
            handleURLModalState={handleURLModalState}
            isFileUploading={isFileUploading}
            handleFileUploadChange={handleFileUploadChange}
            removeUserFromList={removeUserFromList}
            saveEditedImage={saveEditedImage}
            handleRemoveUploadedFile={handleRemoveUploadedFile}
            openAddUserModal={openAddUserModal}
            handleInputChange={handleInputChange}
            handleSaveUrlData={handleSaveUrlData}
            showUrlModal={showUrlModal}
            setURLData={setURLData}
            spaceId={spaceId}
            formValidator={formValidator}
            showGeoLocationModal={showGeoLocationModal}
            setShowGeoLocationModal={setShowGeoLocationModal}
            handleGeoLocationModalState={handleGeoLocationModalState}
          />
        </fieldset>
      </form>
    );
  };

  const getIsUploadDisabled = (id) => {
    const file = fileUploadList[id - 1];
    if (!file) return true;

    const hasCustomFields = template?.some((section) => section.questionFields?.length > 0);

    return (
      file[uploadType.UPLOAD_AS_NEW_VERSION] ||
      file[uploadType.UPLOAD_AS_NEW_FILE] ||
      (hasCustomFields && !file.progressCompleted)
    );
  };

  const gridUploadActionTemplate = (file, id) => {
    if (!file) return null;
    const fileData = fileUploadList[id - 1];

    const localFile = { ...file };

    if (localFile.duplicateFileStatus?.found) {
      localFile.isDuplicate = true;
    }

    const isDisabled = getIsUploadDisabled(id);

    const uploadButton = (
      <div className="flex w-full justify-start">
        <ButtonComponent
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
          onClick={() => handleUpload({ id, name: localFile.name })}
        >
          <span className="flex items-center">{t("Upload")}</span>
        </ButtonComponent>
      </div>
    );

    const uploadAsNewFileOrVersion = (
      <div className="flex w-full justify-start gap-2">
        <ButtonComponent
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
          onClick={() => handleUpload({ id, name: localFile.name }, uploadType.UPLOAD_AS_NEW_FILE)}
          className="flex-1 max-w-200"
          disabled={isDisabled}
        >
          <div className="flex gap-1 items-center">
            {fileUploadList[id - 1]?.[uploadType.UPLOAD_AS_NEW_FILE] && (
              <MdCheckCircleOutline className="w-4 h-4" color="green" />
            )}
            <span className="flex justify-center">{t("Upload as New File")}</span>
          </div>
        </ButtonComponent>

        <ButtonComponent
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
          onClick={() => handleUpload({ id, name: localFile.name }, uploadType.UPLOAD_AS_NEW_VERSION)}
          className="flex-1 max-w-200"
          disabled={isDisabled}
        >
          <div className="flex gap-1 items-center">
            {fileUploadList[id - 1]?.[uploadType.UPLOAD_AS_NEW_VERSION] && (
              <MdCheckCircleOutline className="w-4 h-4" color="green" />
            )}
            <span className="flex justify-center">{t("Upload as New Version")}</span>
          </div>
        </ButtonComponent>
      </div>
    );

    if (fileData?.nomenclature?.length && fileData.isFileNomenclatureCorrect && !batchId) {
      return null;
    } else if (localFile.isDuplicate) {
      if (batchId) {
        return !localFile.isFileNomenclatureCorrect || fileData.nomenclature?.length
          ? uploadButton
          : uploadAsNewFileOrVersion;
      } else {
        return !localFile.isFileNomenclatureCorrect ? null : uploadAsNewFileOrVersion;
      }
    }

    return batchId ? uploadButton : null;
  };

  const uploadEscrowFile = async (id, data, showToast = true) => {
    const file = fileUploadList[id - 1];
    if (file?.uploaded) return false;

    try {
      updateFileData(id, { processing: true });

      const response = await axiosWrapper.post(`${getBackendUrl()}/fileOperations/uploadEscrowFile`, data);

      if (response.data.status) {
        if (batchId) {
          dispatch(removeFileUploadData(id - 1));
        }

        if (showToast) {
          dispatch(
            showToastNotification({
              content: response.data.message,
              type: TOAST_TYPE_SUCCESS,
            }),
          );
        }

        updateFileData(id, { uploaded: true });
        return true;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      if (showToast) {
        dispatch(
          showToastNotification({
            content: error.message,
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
      return false;
    } finally {
      updateFileData(id, { processing: false });
    }
  };

  const handleUpload = async (args, type) => {
    const fileId = args.id;
    const file = fileUploadList[fileId - 1];

    if (!file || file.uploaded || file.processing || file.uploadTriggered) {
      return;
    }

    let name = file.name;
    let extension = "";

    if (name.includes(".")) {
      const parts = name.split(".");
      extension = parts.pop();
    }

    if (file.nomenclature) {
      name = `${file.nomenclature.join(fileNomenclature.separator)}${extension ? "." : ""}${extension}`;
    }

    const data = {
      id: file.fileId,
      name,
    };

    let templateWithAnswers = [];
    if (template?.length) {
      templateWithAnswers = _.cloneDeep(template).map((section) => {
        section.questionFields = section.questionFields.map((q) => {
          if (file[q._id]) {
            q.answer = { value: file[q._id] };
          }
          return q;
        });
        return section;
      });
    }

    const isValid = validateFormWithId(fileId, templateWithAnswers, formValidator);
    if (!isValid) return;

    if (type) {
      data[type] = true;
      updateFileData(fileId, { [type]: true });
    }

    if (file.progressCompleted) {
      const wasUploaded = await uploadEscrowFile(fileId, data);
      if (wasUploaded) {
        updateFileData(fileId, { uploadTriggered: true });
      }
    }
  };

  const saveNomenclature = (args, nomenclatureArr) => {
    let name = args.name;
    let extension = "";

    if (args.name.includes(".")) {
      const fileName = name.split(".");
      extension = fileName.pop();
    }
    const filteredNomenclature = nomenclatureArr.filter((item) => item && item.trim() !== "");

    if (filteredNomenclature.length > 0) {
      name = `${nomenclatureArr.join(fileNomenclature.separator)}${extension ? "." : ""}${extension}`;

      if (fileUploadList[args.id - 1]?.progressCompleted) {
        const isCorrect = isFileNomenclatureCorrect(name, fileNomenclature);
        updateFileData(args.id, {
          nomenclature: nomenclatureArr,
          isFileNomenclatureCorrect: isCorrect,
        });

        if (isCorrect) {
          updateNomenclature(fileUploadList[args.id - 1]?.fileId, name, args);
        }
      } else {
        updateFileData(args.id, {
          nomenclature: nomenclatureArr,
          isFileNomenclatureCorrect: false,
        });
      }
    } else {
      updateFileData(args.id, {
        nomenclature: [],
        isFileNomenclatureCorrect: false,
      });
    }
  };

  const handleNomenclatureChange = (e, args, field, index) => {
    const value = e.target.value;

    const existingNomenclature = fileUploadList[args.id - 1]?.nomenclature || [];
    const requiredLength = fileNomenclature?.fields?.length || 0;
    let nomenclatureArr = [...existingNomenclature];
    while (nomenclatureArr.length < requiredLength) {
      nomenclatureArr.push("");
    }
    nomenclatureArr[index] = value;
    saveNomenclature(args, nomenclatureArr);
  };

  const handleUploadComplete = (file) => {
    const fileData = fileUploadList[file.id - 1];
    if (!fileData || fileData.processed) return;

    const updates = { processed: true };

    if (fileData.customFieldsUpdated && !fileData.customFieldsSaved) {
      const fields = [];
      template?.forEach((section) => {
        section.questionFields.forEach((field) => {
          if (fileData[field._id]) {
            fields.push({
              _id: field._id,
              value: fileData[field._id],
            });
          }
        });
      });

      if (fields.length > 0) {
        saveFormTemplate(file.id, fields, true);
        updates.customFieldsSaved = true;
      }
    }
    updateFileData(file.id, updates);
    if (fileData.nomenclature && !fileData.nomenclatureUpdated) {
      saveNomenclature(file, fileData.nomenclature);
    }

    if (fileData[uploadType.UPLOAD_AS_NEW_FILE] && !fileData.uploaded) {
      handleUpload(file, uploadType.UPLOAD_AS_NEW_FILE);
    } else if (fileData[uploadType.UPLOAD_AS_NEW_VERSION] && !fileData.uploaded) {
      handleUpload(file, uploadType.UPLOAD_AS_NEW_VERSION);
    }
  };

  useEffect(() => {
    const allProcessed = visibleFiles.every((file) => {
      const item = fileUploadList[file.id - 1];
      if (!item || item.isDeleted) return true;

      return item.progressCompleted || item.error || item.isBulkEdit;
    });

    if (setEnableDone) {
      setEnableDone(allProcessed);
    }
  }, [fileUploadList, visibleFiles.length]);

  useEffect(() => {
    fileUploadList.forEach((data, idx) => {
      const id = idx + 1;
      if (data.progressCompleted && !data.uploadCompleteProcessed) {
        handleUploadComplete({ ...data, id });
        updateFileData(id, { uploadCompleteProcessed: true });
      }
    });
  }, [fileUploadList]);

  const asArray = (v) => {
    if (Array.isArray(v)) return v.filter((x) => x != null && x !== "");
    if (v == null) return [];
    if (typeof v === "string") return v ? [v] : [];
    if (typeof v === "object") {
      if (Array.isArray(v.value)) return v.value;
      if (typeof v.value === "string") return v.value ? [v.value] : [];
      return Object.values(v).filter((x) => x != null && x !== "");
    }
    return [];
  };

  const handleCustomFieldData = ({ args, eachField, answerValue }) => {
    const { _id, fieldType } = eachField;
    const value = fieldType === "multi-select" ? asArray(answerValue) : answerValue;

    if (args.id === 0) {
      setBulkData((prev) => ({ ...prev, [_id]: value }));
    } else if (fileUploadList[args.id - 1]) {
      updateFileData(args.id, (data) => ({
        ...data,
        [_id]: value,
        customFieldsUpdated: !data.progressCompleted ? true : data.customFieldsUpdated,
      }));

      if (fileUploadList[args.id - 1]?.fileId || fileUploadList[args.id - 1]?.folderId) {
        setQuery({ id: args.id, eachField, value });
      }
    }
  };

  const saveFormTemplate = async (id, fieldsData = [], progressCompleted) => {
    const itemData = fileUploadList[id - 1] || {};
    const itemId = itemData.isFolder ? itemData.folderId : itemData.fileId;

    let isUploadComplete = true;
    if (!progressCompleted) {
      isUploadComplete = itemData.progressCompleted;
    }

    if (isUploadComplete && fieldsData.length) {
      saveCustomFieldsData({
        id: itemId,
        formTemplateId,
        fieldsData,
        checkResidual: isFolderUpload && fileNomenclature ? false : !batchId,
        isFolder: !!itemData.isFolder,
      });
    }
  };

  const updateNomenclature = async (id, name, args) => {
    updateFileData(args.id, { name: name, nomenclatureUpdated: true });
    if (id && !batchId) {
      await axiosWrapper.post(`${getBackendUrl()}/fileOperations/updateFileNomenclature`, {
        id,
        name,
      });
    }
  };

  const saveCustomFields = async (id, field, value) => {
    const fieldsData = [
      {
        _id: field._id,
        value,
      },
    ];
    saveFormTemplate(id, fieldsData, fileUploadList[id - 1]?.progressCompleted);
  };

  const handleDeleteButtonClick = () => {
    if (selectedFileIds.length > 0) {
      setOpenDeleteConfirmationModal(true);
    }
  };

  const handleBulkUploadButtonClick = async () => {
    const selectedRows = selectedFileIds;
    if (selectedRows.length) {
      let allValid = true;
      for (const id of selectedRows) {
        const file = fileUploadList[id - 1];
        if (!file || !file.folder || !file.name) {
          continue;
        }
        const nomenclatureValid = isFileNomenclatureCorrect(file.name, file.folder.fileNomenclature);

        if (!nomenclatureValid) {
          allValid = false;
          updateFileData(id, {
            error: "File nomenclature does not comply with the nomenclature rule for its Folder.",
            isFileNomenclatureCorrect: false,
          });
          continue;
        } else {
          updateFileData(id, {
            error: "",
            isFileNomenclatureCorrect: true,
          });
        }

        let templateWithAnswers = [];
        if (template && template.length) {
          templateWithAnswers = _.cloneDeep(template).map((section) => {
            const questionFields = section.questionFields.map((question) => {
              if (file[question._id]) {
                question["answer"] = {
                  value: file[question._id],
                };
              }
              return question;
            });
            section.questionFields = questionFields;
            return section;
          });
        }

        const isValid = validateFormWithId(id, templateWithAnswers, formValidator);
        if (!isValid) {
          allValid = false;
          updateFileData(id, { error: "Validation failed for custom fields." });
        }
      }

      if (allValid) {
        for (const id of selectedRows) {
          await handleUpload({
            id,
            name: fileUploadList[id - 1]?.name,
          });
        }
      }
    }
  };

  const collectCustomFieldIds = (template) => {
    const ids = new Set();
    template?.forEach((section) => {
      section.questionFields.forEach((field) => ids.add(field._id));
    });
    return ids;
  };

  const buildBulkPayloads = (customFieldIds, bulkData) => {
    const data = {};
    const fieldsData = [];

    customFieldIds.forEach((fieldId) => {
      if (bulkData[fieldId] === undefined) return;
      data[fieldId] = bulkData[fieldId];
      fieldsData.push({ _id: fieldId, value: bulkData[fieldId] });
    });

    return { data, fieldsData };
  };

  const updateTemplateWithAnswers = (template, safeData) => {
    return _.cloneDeep(template).map((section) => {
      section.questionFields = section.questionFields.map((q) => {
        if (safeData[q._id]) q.answer = { value: safeData[q._id] };
        return q;
      });
      return section;
    });
  };

  const applyBulkToItem = ({
    index,
    data,
    fieldsData,
    template,
    dispatch,
    saveFormTemplate,
    validateFormWithId,
    formValidator,
  }) => {
    const safeData = { ...data, customFieldsUpdated: true };

    dispatch(updateFileUploadData({ index, data: safeData }));
    saveFormTemplate(index + 1, fieldsData);

    const templateWithAnswers = updateTemplateWithAnswers(template, safeData);
    validateFormWithId(index + 1, templateWithAnswers, formValidator);
  };

  const resetBulkState = (setBulkData, setIsBulk) => {
    setBulkData({ id: 0, progress: 0, extension: null, name: "", isBulkEdit: true });
    setIsBulk(false);
  };

  const toggleBulkUpdateButton = () => {
    if (!isBulk) {
      setIsBulk(true);
      setSelectedFileIds([]);
      return;
    }

    const customFieldIds = collectCustomFieldIds(template);

    fileUploadList.forEach((item, index) => {
      const { data, fieldsData } = buildBulkPayloads(customFieldIds, bulkData);
      if (fieldsData.length === 0) return;

      applyBulkToItem({
        index,
        data,
        fieldsData,
        template,
        dispatch,
        saveFormTemplate,
        validateFormWithId,
        formValidator,
      });
    });

    resetBulkState(setBulkData, setIsBulk);
    setSelectedFileIds([]);
  };

  const successDeleteToast = () => {
    dispatch(
      showToastNotification({
        content: t("File(s) deleted successfully."),
        type: TOAST_TYPE_SUCCESS,
      }),
    );
  };

  const handleDeleteModalClose = () => {
    setOpenDeleteConfirmationModal(false);
  };

  function removeFilesByIds(ids) {
    ids.forEach((id) => {
      if (typeof id === "number") {
        dispatch(removeFileUploadData(id - 1));
      }
    });
  }

  const handleDeleteSuccess = (indexes) => {
    setSelectedFileIds([]);
    handleDeleteModalClose();
    removeFilesByIds(indexes);
    successDeleteToast();
  };

  const onDeleteFiles = () => {
    if (!batchId) {
      visibleFiles
        .filter((data) => data.id != 0 && selectedFileIds.includes(data.id))
        .forEach((file) => {
          if (fileUploadList[file.id - 1]?.controller) {
            fileUploadList[file.id - 1]?.controller.forEach((item) => item.abort());
          }
        });
    }
  };

  const getFilesGrid = () => (
    <div className="overflow-auto h-full">
      <FilesTable
        fileUploadList={visibleFiles}
        selectedFileIds={selectedFileIds}
        setSelectedFileIds={setSelectedFileIds}
        template={template}
        gridNomenclatureTemplate={gridNomenclatureTemplate}
        progressTemplate={progressTemplate}
        gridNameTemplate={gridNameTemplate}
        gridCustomFieldTemplate={gridCustomFieldTemplate}
        gridUploadActionTemplate={gridUploadActionTemplate}
        fileNomenclature={fileNomenclature}
      />
    </div>
  );

  return (
    <div className="h-full overflow-auto flex flex-col" id="formData">
      <div className="p-4 flex gap-4">
        {!isFileVersionUploading && (
          <ButtonComponent
            onClick={handleDeleteButtonClick}
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            disabled={disableDeleteButton}
          >
            <span className="flex items-center gap-2">
              <MdDeleteOutline className="h-4" />
              Delete
            </span>
          </ButtonComponent>
        )}
        {!isFileVersionUploading && template && template.length ? (
          !isBulk ? (
            <ButtonComponent onClick={toggleBulkUpdateButton} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
              <span className="flex items-center gap-2">
                <MdOutlineEdit className="h-4" />
                Bulk Edit
              </span>
            </ButtonComponent>
          ) : (
            <ButtonComponent onClick={toggleBulkUpdateButton} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
              <span className="flex items-center gap-2">
                <FaRegSave className="h-4" />
                Update All
              </span>
            </ButtonComponent>
          )
        ) : null}
        {batchId ? (
          <ButtonComponent
            onClick={handleBulkUploadButtonClick}
            buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
            disabled={disableBulkUploadButton}
          >
            <span className="flex items-center gap-2">
              <img src={Upload} className="h-4" />
              Bulk Upload
            </span>
          </ButtonComponent>
        ) : null}
      </div>
      {getFilesGrid()}
      {openDeleteConfirmationModal && (
        <div style={{ marginInlineStart: "-40px" }}>
          <FileDelete
            workspaceId={workspaceId}
            selectedFiles={visibleFiles
              .filter((data) => data.id != 0 && selectedFileIds.includes(data.id))
              .map((file) => {
                if (fileUploadList[file.id - 1].isFolder) {
                  file.hierarchyId = fileUploadList[file.id - 1]?.folderId;
                } else if (fileUploadList[file.id - 1]) {
                  file.hierarchyId = fileUploadList[file.id - 1]?.fileId;
                }
                return file;
              })}
            onCloseModal={handleDeleteModalClose}
            onDeleteSuccess={() => handleDeleteSuccess(selectedFileIds)}
            isFileUpload
            onDeleteFiles={onDeleteFiles}
          />
        </div>
      )}
    </div>
  );
};

FilesGrid.propTypes = {
  template: PropTypes.arrayOf(
    PropTypes.shape({
      questionFields: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string,
          questionValue: PropTypes.string,
        }),
      ),
    }),
  ),
  formTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  workspaceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setEnableDone: PropTypes.func,
  fileNomenclature: PropTypes.shape({
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
      }),
    ),
    separator: PropTypes.string,
  }),
  batchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isFolderUpload: PropTypes.bool,
  formValidator: PropTypes.object,
};

FilesGrid.defaultProps = {
  template: [],
  formTemplateId: undefined,
  workspaceId: undefined,
  setEnableDone: undefined,
  fileNomenclature: { fields: [], separator: "-" },
  batchId: undefined,
  isFolderUpload: false,
  formValidator: undefined,
};

export default FilesGrid;
